import React, { useContext, useEffect, useRef, useState } from 'react';
import { CreditCard, Eye, Receipt, X } from 'tabler-icons-react';
import dayjs from 'dayjs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Badge } from '@mantine/core';
import PropTypes from 'prop-types';
import TableView from '../../common/TableView';
import {
  ECOM_ORDER_STATUS_ENUM,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import { useModalState, usePaginationFilter } from '../../../helpers/hooks';
import { currencyFormat, formatUtcDate } from '../../../helpers/format';
import { getOrderSummaryInfo } from '../../../helpers/storeHelper';
import { triggerNotification } from '../../../helpers/notificationHelper';
import AppCard from '../../common/AppCard';
import EcomOrderTransactionModal from './EcomOrderTransactionModal';

const VIEW_ACTIONS = [
  {
    label: 'View order',
    value: VIEW_ACTIONS_ENUM.VIEW,
    section: 1,
    icon: <Eye size={18} />
  },
  {
    label: 'View transaction',
    value: 'transaction',
    section: 1,
    icon: <Receipt size={18} />
  },
  {
    label: 'Cancel',
    value: 'cancel',
    section: 2,
    color: '#C40000',
    icon: <X color="#C40000" size={18} />,
    isVisible: ({ isCancelled, isBulkOrders }) => !isCancelled && !isBulkOrders
  },
  {
    label: 'Refund',
    value: VIEW_ACTIONS_ENUM.REFUND,
    section: 2,
    color: '#C40000',
    icon: <CreditCard color="#C40000" size={18} />,
    isVisible: ({ isRefunded, isBulkOrders }) => !isRefunded && !isBulkOrders
  }
];

const TABLE_COLUMNS = [
  {
    label: 'Order',
    value: 'order',
    sortable: true
  },
  {
    label: 'Date',
    value: 'date',
    sortable: true
  },
  {
    label: 'Fulfillment status',
    value: 'status',
    sortable: true
  },
  {
    label: 'Payment status',
    value: 'payment_status',
    sortable: true
  },
  {
    label: 'Customer',
    value: 'customer',
    sortable: true
  },
  {
    label: 'Products ordered',
    value: 'products',
    sortable: true
  },
  {
    label: 'Total paid',
    value: 'total',
    sortable: true
  }
];

const EcomStoreOrderTable = ({ pkRegAssociation }) => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const { state, fetchPaginatedStoreOrders } = useContext(
    RegistrationAdminContext
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    pageIndex,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data: orderData,
    filter,
    loading: isDataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh,
    onReset
  } = usePaginationFilter(
    {
      search: '',
      status: '',
      sort: 'order_desc'
    },
    (newFilter) => {
      fetchPaginatedStoreOrders(pkRegAssociation, newFilter, null, (error) => {
        triggerNotification(error);
      });
    },
    state.paginatedEcomOrders
  );
  const loading = !hasFetched.current || isDataLoading;
  const hasOrders = !loading && totalNoFilterCount > 0;
  const selectedOrders = orderData.filter((d) =>
    selectedRows.includes(d.pkEcomOrder)
  );
  const baseUrl = '/admin/financials/merchandise';

  useEffect(() => {
    if (pkRegAssociation) {
      onReset({
        search: new URLSearchParams(search).get('customer') || '',
        escrowOrders: new URLSearchParams(search).get('type') === 'escrow',
        sort: 'order_desc'
      });
      hasFetched.current = true;
    }
  }, [pkRegAssociation]);

  return (
    <AppCard style={{ flex: 1, padding: 0, height: '100%' }}>
      <TableView
        columns={TABLE_COLUMNS}
        disabled={!loading && totalNoFilterCount === 0}
        disabledContent={{
          title: 'No orders found.',
          description:
            'Orders will appear here once customers begin to place orders.',
          icon: <Receipt color="#000" size={125} />
        }}
        emptyMessage="No orders found."
        enableInputsWhenLoading
        filters={[
          {
            key: 1,
            label: 'Search',
            placeholder: 'Filter by customer',
            value: filter.search || '',
            onChange: (v) =>
              onFilterChange({
                ...filter,
                search: v
              })
          },
          {
            key: 2,
            label: 'Status',
            type: 'select',
            value: filter.status || null,
            placeholder: 'Filter by status',
            searchable: true,
            clearable: true,
            data: [
              { value: 'unfulfilled', label: 'Unfulfilled' },
              { value: 'fulfilled', label: 'Fulfilled' },
              { value: 'refunded', label: 'Refunded' },
              { value: 'cancelled', label: 'Cancelled' }
            ],
            onChange: (value) =>
              onFilterChange({ ...filter, status: value }, true)
          }
        ]}
        isDescendingSort={isDescendingSort}
        isLoading={loading}
        noSort
        onAction={(action, item) => {
          if (action === VIEW_ACTIONS_ENUM.VIEW) {
            navigate(`${baseUrl}/${item.key}`);
          }
          else if (action === VIEW_ACTIONS_ENUM.REFUND) {
            navigate(`${baseUrl}/${item.key}/refund`);
          }
          else if (action === 'cancel') {
            navigate(`${baseUrl}/${item.key}?modal=cancel`);
          }
          else {
            onOpenModal(action, [
              orderData.find((o) => o.pkEcomOrder === item.key)
            ]);
          }
        }}
        onChangeSortBy={onSortChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
        onSelect={setSelectedRows}
        pageIndex={pageIndex}
        rows={orderData.map((d) => {
          const {
            completelyRefunded,
            bulkInfo,
            productInfo,
            isFullyCancelled,
            totalPaid,
            statusInfo,
            paymentStatusInfo,
            externalOrderInfo,
            isMissingExternalOrder
          } = getOrderSummaryInfo(d);

          return {
            key: d.pkEcomOrder,
            actions: VIEW_ACTIONS.filter(
              (f) =>
                !f.isVisible ||
                f.isVisible({
                  isCancelled: isFullyCancelled,
                  isRefunded: completelyRefunded,
                  isAccumulating:
                    d.fkEcomOrderStatus.toString() ===
                    ECOM_ORDER_STATUS_ENUM.ACCUMULATING,
                  externalOrderInfo,
                  isMissingExternalOrder
                })
            ),
            columns: [
              {
                key: 1,
                label: `#${d.pkEcomOrder}`,
                weight: 500,
                component: Link,
                to: `${baseUrl}/${d.pkEcomOrder}`
              },
              {
                key: 3,
                label: dayjs(formatUtcDate(d.purchasedAt)).format(
                  'MMMM D, YYYY h:mm A'
                )
              },
              {
                key: 4,
                customComponent: (
                  <Badge color={statusInfo.color} variant="light">
                    {statusInfo.label}
                  </Badge>
                )
              },
              {
                key: 5,
                customComponent: (
                  <Badge color={paymentStatusInfo.color} variant="light">
                    {paymentStatusInfo.label}
                  </Badge>
                )
              },
              {
                key: 8,
                label: d.ecomCustomer.user?.name,
                weight: 500,
                subLabel: d.ecomCustomer.email,
                component: Link,
                to: `${baseUrl}/${d.pkEcomOrder}`
              },
              {
                key: 9,
                label: productInfo.itemCount + bulkInfo.itemCount,
                sortValue: productInfo.itemCount + bulkInfo.itemCount
              },
              {
                key: 10,
                label: currencyFormat(totalPaid / 100)
              }
            ]
          };
        })}
        selectedRows={selectedRows}
        selectInputOnly
        sortBy={filter.sort || TABLE_COLUMNS[0].value}
        tableTitle="Orders"
        totalCount={totalCount}
        totalPages={totalPages}
      />

      <EcomOrderTransactionModal
        isOpen={modalState.isOpen && modalState.action === 'transaction'}
        onClose={onCloseModal}
        onShowRefund={() =>
          onOpenModal(VIEW_ACTIONS_ENUM.REFUND, modalState.item[0])
        }
        pkEcomOrder={
          modalState.action === 'transaction' ? modalState.item[0] : null
        }
      />
    </AppCard>
  );
};

EcomStoreOrderTable.propTypes = {pkRegAssociation: PropTypes.oneOfType([PropTypes.string, PropTypes.number])};

export default EcomStoreOrderTable;
